/** "Setup N of M complete — Continue setup →" banner for the tenant detail page.
 *
 *  Reads the same items query the Preflight tab / setup page share (no extra
 *  polling; cheap when cached). Renders nothing once every applicable item is
 *  done, when the viewer lacks the preflight perm, or while loading.
 */
import { Alert, Button } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import { items as itemsApi } from '@/api/preflight';
import { usePermissions } from '@/hooks/usePermissions';

export function SetupProgressBanner({ topId }: { topId: number }) {
  const { hasPerm } = usePermissions();
  const enabled = hasPerm('console_preflight_view');
  const query = useQuery({
    queryKey: ['preflight', 'items', topId],
    queryFn: () => itemsApi.list(topId),
    enabled,
  });
  if (!enabled || !query.data) return null;
  const applicable = query.data.filter((i) => i.applicable);
  const done = applicable.filter((i) => i.status === 'completed').length;
  const required = applicable.filter((i) => !i.optional);
  if (required.length === 0 || required.every((i) => i.status === 'completed')) return null;
  return (
    <Alert
      severity="info"
      sx={{ mb: 2 }}
      action={
        <Button size="small" component={RouterLink} to={`/tour-operators/${topId}/setup`}>
          Continue setup →
        </Button>
      }
    >
      Setup {done} of {applicable.length} complete.
    </Alert>
  );
}
